import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText, Film, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, DOMAINES, domaineLabel, errMsg, fmtDate, mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DevisPublicDialog = ({ item, onClose }) => {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", ville: "", message: "", site_web: "" });
  const [busy, setBusy] = useState(false);
  const ouvertA = useRef(Date.now());
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (item) ouvertA.current = Date.now();
  }, [item]);

  const envoyer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads/public", {
        ...form,
        realisation_id: item?.id || null,
        message: form.message.trim() || `Devis similaire à : ${item?.titre || "—"}`,
        duree_ms: Date.now() - ouvertA.current,
      });
      toast.success(data.message);
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="devis-public-dialog" aria-describedby="devis-public-desc">
        <DialogHeader>
          <DialogTitle className="font-display">Demander un devis</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600" id="devis-public-desc">
            Réalisation de référence : <strong>{item?.titre}</strong>. Laissez vos coordonnées, BKPRO vous rappelle.
          </p>
          <Input data-testid="devis-public-nom" value={form.nom} onChange={set("nom")} placeholder="Votre nom *" className="h-12" />
          <input
            type="text"
            name="site_web"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.site_web}
            onChange={set("site_web")}
            className="absolute left-[-9999px] h-0 w-0 opacity-0"
          />
          <Input data-testid="devis-public-email" value={form.email} onChange={set("email")} placeholder="Email" className="h-12" />
          <Input data-testid="devis-public-tel" value={form.telephone} onChange={set("telephone")} placeholder="Téléphone" className="h-12" />
          <Input data-testid="devis-public-ville" value={form.ville} onChange={set("ville")} placeholder="Ville du projet" className="h-12" />
          <Textarea data-testid="devis-public-message" value={form.message} onChange={set("message")} rows={4} placeholder="Votre besoin, surface, délai souhaité…" />
          <Button
            data-testid="devis-public-submit-btn"
            onClick={envoyer}
            disabled={busy || form.nom.trim().length < 2}
            className="w-full h-12 rounded-xl bg-[#005CFF] hover:bg-[#0047CC]"
          >
            {busy ? "Envoi…" : "Envoyer ma demande"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function VitrinePublique() {
  const [items, setItems] = useState([]);
  const [domaine, setDomaine] = useState(null);
  const [devisItem, setDevisItem] = useState(null);

  useEffect(() => {
    api
      .get("/realisations", { params: domaine ? { domaine } : {} })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, [domaine]);

  useEffect(() => {
    document.title = "BKPRO — Nos réalisations : électricité, vidéosurveillance, Loxone, clim, réseau, photovoltaïque";
  }, []);

  const partager = async (r) => {
    const url = `${API}/vitrine/${r.id}/partage`;
    const contenu = { title: `${r.titre} — BKPRO`, text: r.description || "Réalisation BKPRO", url };
    if (navigator.share) {
      try {
        await navigator.share(contenu);
        return;
      } catch {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien de partage copié");
    } catch {
      toast.error("Copie impossible, lien : " + url);
    }
  };

  return (
    <div className="min-h-screen bk-grid-bg" data-testid="vitrine-publique-page">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <span className="font-display text-lg font-black tracking-tighter text-slate-900">BKPRO</span>
          <Link
            to="/login"
            data-testid="vitrine-publique-login-link"
            className="text-sm font-semibold text-[#005CFF] hover:underline inline-flex items-center gap-1.5"
          >
            Espace client <ArrowRight size={15} />
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-12 space-y-9">
        <div>
          <div className="bk-label mb-2">Électricité · Vidéosurveillance · Loxone · Climatisation · Réseau · Photovoltaïque</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.03]">
            Nos installations,
            <br />
            chantier par chantier.
          </h1>
          <p className="text-base text-slate-600 mt-4 max-w-2xl">
            Parcourez nos réalisations et demandez un devis en 30 secondes — sans créer de compte.
          </p>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl" data-testid="vitrine-pub-note-equipe">
            Nous, on fait des pauses — pas BKPROD. Notre équipe est humaine et souvent en intervention, mais nos clients
            sont accompagnés en continu par notre application : dépannage guidé, notices et suivi des demandes, même
            bureau fermé.
          </p>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            data-testid="vitrine-pub-filter-all"
            onClick={() => setDomaine(null)}
            className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${!domaine ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            Tous
          </button>
          {DOMAINES.map((d) => (
            <button
              key={d.key}
              data-testid={`vitrine-pub-filter-${d.key}`}
              onClick={() => setDomaine(d.key)}
              className={`shrink-0 text-sm px-4 py-2 rounded-full border transition-colors ${domaine === d.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {items.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="vitrine-pub-empty">
            Aucune réalisation publiée pour ce domaine pour le moment.
          </p>
        )}

        <div className="grid sm:grid-cols-2 gap-5">
          {items.map((r, i) => (
            <motion.article
              key={r.id}
              data-testid={`vitrine-pub-card-${r.id}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.05, 0.4) }}
              className="rounded-2xl overflow-hidden border border-slate-200 bg-white flex flex-col"
            >
              {r.image_url && <img src={r.image_url} alt={r.titre} className="h-44 w-full object-cover" />}
              <div className="p-5 flex flex-col flex-1">
                <div className="bk-label mb-2">
                  {domaineLabel(r.domaine)} {r.ville ? `· ${r.ville}` : ""}
                </div>
                <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{r.titre}</h2>
                <p className="text-sm text-slate-600 mt-2 flex-1">{r.description}</p>
                <p className="text-xs text-slate-400 mt-2">{fmtDate(r.created_at)}</p>
                {r.video_url && (
                  <a
                    href={mediaUrl(r.video_url)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`vitrine-pub-video-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#005CFF] hover:underline mt-3"
                  >
                    <Film size={13} /> Voir la vidéo du chantier
                  </a>
                )}
                <Button
                  data-testid={`vitrine-pub-devis-btn-${r.id}`}
                  onClick={() => setDevisItem(r)}
                  variant="outline"
                  className="mt-4 h-11 rounded-xl border-slate-300"
                >
                  <FileText size={15} className="mr-2" /> Demander un devis similaire
                </Button>
                <button
                  data-testid={`vitrine-pub-share-btn-${r.id}`}
                  onClick={() => partager(r)}
                  className="mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#005CFF] transition-colors"
                >
                  <Share2 size={13} /> Partager cette réalisation
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </main>

      <DevisPublicDialog item={devisItem} onClose={() => setDevisItem(null)} />
    </div>
  );
}
